import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import Link from 'next/link'
import { Instagram, Mail, MessageCircle, Phone } from 'lucide-react'
import { BrandLogo } from '@/components/generals/brand-logo'
import { clinic, navLinks } from '@/constants/clinic'

const footerContacts = [
    { label: clinic.phone.label, href: clinic.phone.href, icon: Phone, external: false },
    { label: clinic.whatsapp.label, href: clinic.whatsapp.href, icon: MessageCircle, external: true },
    { label: clinic.email.label, href: clinic.email.href, icon: Mail, external: false },
    { label: clinic.instagram.label, href: clinic.instagram.href, icon: Instagram, external: true }
]

export const SiteFooter: FC = (): ReactNode => {
    return (
        <footer className='bg-ink text-white'>
            <div className='mx-auto max-w-7xl px-4 pt-16 pb-28 sm:px-6 md:pb-10 lg:px-8'>
                <div className='grid gap-12 sm:grid-cols-2 lg:grid-cols-[1.3fr_0.7fr_1.1fr_1.1fr] lg:gap-10'>
                    <div>
                        <BrandLogo tone='light' />

                        <p className='mt-6 font-semibold'>
                            {clinic.name}
                        </p>

                        <p className='mt-2 text-sm text-white/65'>
                            {clinic.accreditationShort}
                        </p>

                        <p className='mt-1 text-sm text-white/65'>
                            {clinic.license}
                        </p>
                    </div>

                    <nav aria-label='Menu footer'>
                        <h2 className='text-sm font-semibold text-white'>
                            Menu
                        </h2>

                        <ul className='mt-4 grid gap-3 text-sm'>
                            {navLinks.map(link => (
                                <li key={link.id}>
                                    <a href={link.href} className='text-white/70 transition-colors hover:text-white'>
                                        {link.label}
                                    </a>
                                </li>
                            ))}

                            <li>
                                <a href='/#jam-layanan' className='text-white/70 transition-colors hover:text-white'>
                                    Jam Layanan
                                </a>
                            </li>

                            <li>
                                <Link href='/masuk' className='text-white/70 transition-colors hover:text-white'>
                                    Masuk
                                </Link>
                            </li>

                            <li>
                                <Link href='/daftar' className='text-white/70 transition-colors hover:text-white'>
                                    Daftar
                                </Link>
                            </li>
                        </ul>
                    </nav>

                    <div>
                        <h2 className='text-sm font-semibold text-white'>
                            Kontak
                        </h2>

                        <ul className='mt-4 grid gap-3 text-sm'>
                            {footerContacts.map(item => {
                                const Icon = item.icon

                                return (
                                    <li key={item.label}>
                                        <a
                                            href={item.href}
                                            target={item.external ? '_blank' : undefined}
                                            rel={item.external ? 'noopener noreferrer' : undefined}
                                            className='inline-flex max-w-full items-center gap-2.5 text-white/70 transition-colors hover:text-white'
                                        >
                                            <Icon className='size-4 shrink-0 text-brand-300' aria-hidden='true' />
                                            <span className='break-all'>
                                                {item.label}
                                            </span>
                                        </a>
                                    </li>
                                )
                            })}
                        </ul>
                    </div>

                    <div>
                        <div className='flex items-center gap-4 rounded-2xl bg-white/5 p-4 ring-1 ring-white/10'>
                            <span className='relative size-14 shrink-0 overflow-hidden rounded-full bg-white ring-2 ring-white/20'>
                                <Image
                                    src='/images/logo-yayasan.png'
                                    alt={`Logo ${clinic.foundation}`}
                                    fill
                                    sizes='56px'
                                    className='object-cover'
                                />
                            </span>

                            <div>
                                <p className='text-sm text-white/65'>
                                    Di bawah naungan
                                </p>

                                <p className='font-semibold'>
                                    {clinic.foundation}
                                </p>
                            </div>
                        </div>

                        <p className='mt-5 text-sm leading-relaxed text-white/65'>
                            {clinic.address}
                        </p>
                    </div>
                </div>

                <div className='mt-14 flex flex-col gap-3 border-t border-white/10 pt-6 text-sm text-white/55 md:flex-row md:items-center md:justify-between'>
                    <p>
                        Proyek akademik. Seluruh data jadwal, kuota, dan tarif pada aplikasi ini adalah data demo.
                    </p>

                    <p>
                        © 2026
                        {' '}
                        {clinic.name}
                    </p>
                </div>
            </div>
        </footer>
    )
}
